import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from './ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { 
  Bell, 
  BellRing, 
  Check, 
  CheckCheck,
  Trash2, 
  FileText, 
  Briefcase, 
  Calendar, 
  Users, 
  MessageSquare,
  ClipboardList,
  Building2,
  Clock,
  Package
} from 'lucide-react';
import axios from 'axios';
import { connectRealtime } from '../utils/realtimeClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Bildirim kategorilerine göre ikon ve renk
const getCategoryInfo = (category, type) => {
  const categoryMap = {
    gorevler: { icon: ClipboardList, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
    taleplerim: { icon: FileText, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    iletisim: { icon: MessageSquare, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
    raporlar: { icon: FileText, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
    is_programi: { icon: Briefcase, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200' },
    takvim: { icon: Calendar, color: 'text-cyan-600', bg: 'bg-cyan-50', border: 'border-cyan-200' },
    sube_bilgileri: { icon: Building2, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
    mesai_takip: { icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
    sube_ici_gorev: { icon: Users, color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200' },
    zimmet: { icon: Package, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  };
  
  // Bildirim tipine göre özel renkler
  if (type?.includes('approved') || type?.includes('accepted') || type?.includes('completed')) {
    return { icon: Check, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' };
  }
  if (type?.includes('rejected')) {
    return { icon: FileText, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
  }
  
  return categoryMap[category] || { icon: Bell, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' };
};

const NotificationPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [counts, setCounts] = useState({ total: 0 });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const [notifRes, countsRes] = await Promise.all([
        axios.get(`${API}/notifications?limit=100`, { headers: getAuthHeaders() }),
        axios.get(`${API}/notifications/counts`, { headers: getAuthHeaders() })
      ]);
      setNotifications(notifRes.data || []);
      setCounts(countsRes.data || { total: 0 });
    } catch (error) {
      console.error('Bildirimler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    // Gerçek zamanlı bağlantıyı başlat
    connectRealtime();
    // WebSocket'ten gelen sinyallerde anında yenile
    const onRealtime = () => loadNotifications();
    window.addEventListener('unread-counts-updated', onRealtime);
    window.addEventListener('fortis:new-notification', onRealtime);
    // Yedek: her 60 saniyede bir kontrol et (WS kopması ihtimaline karşı)
    const interval = setInterval(loadNotifications, 60000);
    return () => {
      clearInterval(interval);
      window.removeEventListener('unread-counts-updated', onRealtime);
      window.removeEventListener('fortis:new-notification', onRealtime);
    };
  }, [loadNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setCounts(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    } catch (error) {
      console.error('Bildirim işaretlenirken hata:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setCounts({ total: 0 });
      toast.success('Tüm bildirimler okundu olarak işaretlendi');
    } catch (error) {
      toast.error('Bildirimler işaretlenirken hata oluştu');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: getAuthHeaders()
      });
      const removed = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (removed && !removed.is_read) {
        setCounts(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      }
      toast.success('Bildirim silindi');
    } catch (error) {
      toast.error('Bildirim silinemedi');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Az önce';
      if (diffMins < 60) return `${diffMins} dk önce`;
      if (diffHours < 24) return `${diffHours} saat önce`;
      if (diffDays < 7) return `${diffDays} gün önce`;
      
      return date.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'short'
      });
    } catch {
      return '';
    }
  };

  const unreadCount = counts.total || 0;
  const hasUnread = unreadCount > 0;

  const filteredNotifications = notifications.filter(n => {
    if (activeTab === 'all') return true;
    if (activeTab === 'tasks') return ['gorevler', 'sube_ici_gorev', 'is_programi'].includes(n.category);
    if (activeTab === 'requests') return ['taleplerim', 'raporlar'].includes(n.category);
    if (activeTab === 'depot') return ['zimmet'].includes(n.category);
    return true;
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative"
          data-testid="notification-bell"
        >
          {hasUnread ? (
            <BellRing className="h-5 w-5 text-[hsl(var(--brand-700))] animate-pulse" />
          ) : (
            <Bell className="h-5 w-5 text-gray-600" />
          )}
          {hasUnread && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs bg-red-500"
              data-testid="notification-count"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="end">
        <div className="p-3 border-b bg-gradient-to-r from-[hsl(var(--brand-50))] to-white">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Bell className="h-4 w-4 text-[hsl(var(--brand-700))]" />
              Bildirimler
              {hasUnread && (
                <Badge variant="secondary" className="text-xs">
                  {unreadCount} yeni
                </Badge>
              )}
            </h3>
            <div className="flex items-center gap-1">
              {hasUnread && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={markAllAsRead}
                  className="h-7 px-2 text-xs text-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-50))]"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="h-3 w-3 mr-1" />
                  Tümünü Oku
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={loadNotifications}
                className="h-7 px-2 text-xs"
                disabled={loading}
              >
                {loading ? '...' : 'Yenile'}
              </Button>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-3 pt-2 pb-1 border-b">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all" className="text-xs">Tümü</TabsTrigger>
              <TabsTrigger value="tasks" className="text-xs">Görevler</TabsTrigger>
              <TabsTrigger value="requests" className="text-xs">Talepler</TabsTrigger>
              <TabsTrigger value="depot" className="text-xs">Depo</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value={activeTab} className="m-0 border-0 p-0 outline-none">
            <ScrollArea className="max-h-[400px]">
              {filteredNotifications.length > 0 ? (
                <div className="p-2 space-y-2">
                  {filteredNotifications.map((notification) => {
                const categoryInfo = getCategoryInfo(notification.category, notification.type);
                const Icon = categoryInfo.icon;
                
                return (
                  <div 
                    key={notification.id}
                    className={`p-3 rounded-lg border transition-all ${
                      notification.is_read 
                        ? 'bg-white border-gray-100 opacity-70' 
                        : `${categoryInfo.bg} ${categoryInfo.border}`
                    }`}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-full ${notification.is_read ? 'bg-gray-100' : categoryInfo.bg}`}>
                        <Icon className={`h-4 w-4 ${notification.is_read ? 'text-gray-400' : categoryInfo.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${notification.is_read ? 'text-gray-500' : 'text-gray-800'}`}>
                          {notification.title}
                        </p>
                        {notification.message && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {!notification.is_read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-gray-400 hover:text-[hsl(var(--brand-700))]"
                            onClick={() => markAsRead(notification.id)}
                            title="Okundu olarak işaretle"
                            data-testid={`mark-read-${notification.id}`}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                          onClick={() => deleteNotification(notification.id)}
                          title="Bildirimi sil"
                          data-testid={`delete-notification-${notification.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-3 text-gray-200" />
              <p className="text-sm font-medium">Bildirim yok</p>
              <p className="text-xs mt-1 text-gray-400">
                Bu kategoride yeni bildirim bulunmuyor
              </p>
            </div>
          )}
        </ScrollArea>
        </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationPanel;
